import {Html, Text} from '@react-three/drei';
import {useFrame} from '@react-three/fiber';
import {Interactive} from '@react-three/xr';
import {forwardRef, useEffect, useRef, useState} from 'react';
import {Group, Mesh, Vector3} from 'three';
import {ballPositionStore} from '../../hooks/useXRController';
import {useStore} from '../../store/useStore';
import {EyeToTrain} from '../../types/exercises';
import {Layer} from '../../types/layers';
import {workoutStore} from '../app/WorkoutSequence';
import {VRSettingsMenu} from './VRSettingsMenu';

interface TrainingTargetProps {
  position: Vector3;
  scale?: number;
  showPeripheral?: boolean;
  eyeToTrain?: EyeToTrain;
  isRedGreen?: boolean;
  isMFBF?: boolean;
  isOverlay?: boolean;
  inXRSession?: boolean;
  onPositionChange?: (newOffset: Vector3) => void;
  onMenuToggle?: (isOpen: boolean) => void;
  onEndExercise?: () => void;
  timeRemaining?: number;
  onSpeedChange?: (speed: number) => void;
  onRangeChange?: (range: 'narrow' | 'medium' | 'wide') => void;
  onDurationChange?: (duration: number) => void;
  currentSpeed?: number;
  currentRange?: 'narrow' | 'medium' | 'wide';
  currentDuration?: number;
  // Per-eye settings for preview mode
  leftEyeColor?: string;
  rightEyeColor?: string;
  leftEyeOverlay?: boolean;
  rightEyeOverlay?: boolean;
}

export const TrainingTarget = forwardRef<Group, TrainingTargetProps>(
  ({
    position,
    scale = 1,
    showPeripheral = false,
    eyeToTrain = 'both',
    inXRSession = false,
    onPositionChange,
    onMenuToggle,
    onEndExercise,
    onSpeedChange,
    onRangeChange,
    onDurationChange,
    currentSpeed = 7.5,
    currentRange = 'medium',
    currentDuration = 10,
    leftEyeColor,
    rightEyeColor,
    leftEyeOverlay,
    rightEyeOverlay,
  }) => {
    const targetRef = useRef<Mesh>(null);
    const groupRef = useRef<Group>(null);
    const {isDevMode} = useStore();
    const [rotation, setRotation] = useState({x: 0, y: 0});

    // State for position adjustments relative to camera
    const [positionOffset, setPositionOffset] = useState({x: 0, y: 0, z: 0});
    const [showAdjustmentUI, setShowAdjustmentUI] = useState(false);
    const [showDirectionMenu, setShowDirectionMenu] = useState(false);
    const [menuMode, setMenuMode] = useState<'position' | 'settings'>('position');
    const [isDragging, setIsDragging] = useState(false);
    const [sliderValue, setSliderValue] = useState(0);

    // VR session state (only used when inXRSession is true)
    // Initialize from props if available, otherwise use defaults
    const [vrLeftEyeColor, setVrLeftEyeColor] = useState(leftEyeColor ?? 'red');
    const [vrRightEyeColor, setVrRightEyeColor] = useState(rightEyeColor ?? 'lime');
    const [vrLeftEyeOverlay, setVrLeftEyeOverlay] = useState(leftEyeOverlay ?? false);
    const [vrRightEyeOverlay, setVrRightEyeOverlay] = useState(rightEyeOverlay ?? false);

    // Determine final colors and overlays based on mode
    // In VR mode: use VR state (can be changed via menu)
    // In preview mode: use explicit props if provided, otherwise sensible defaults
    const finalLeftEyeColor = inXRSession
      ? vrLeftEyeColor
      : (leftEyeColor ?? 'red');

    const finalRightEyeColor = inXRSession
      ? vrRightEyeColor
      : (rightEyeColor ?? 'lime');

    const finalLeftEyeOverlay = inXRSession
      ? vrLeftEyeOverlay
      : (leftEyeOverlay ?? false);

    const finalRightEyeOverlay = inXRSession
      ? vrRightEyeOverlay
      : (rightEyeOverlay ?? false);
    const dragStartPosition = useRef<{
      x: number;
      y: number;
      z: number;
      controllerX: number;
      controllerY: number;
      controllerZ: number;
    } | null>(null);

    // Update position offset with callback if provided
    const updatePositionOffset = (newOffset: {x: number; y: number; z: number}) => {
      setPositionOffset(newOffset);

      if (onPositionChange) {
        onPositionChange(new Vector3(newOffset.x, newOffset.y, newOffset.z));
      }

      // Show adjustment UI and hide after delay
      setShowAdjustmentUI(true);
      setTimeout(() => {
        setShowAdjustmentUI(false);
      }, 3000);
    };

    // Move target in specified direction
    const moveTarget = (direction: 'up' | 'down' | 'left' | 'right' | 'closer' | 'farther') => {
      const movementAmount = 0.15; // Larger increment for easier VR adjustment
      let newOffset = {...positionOffset};

      switch (direction) {
        case 'up':
          newOffset.y += movementAmount;
          break;
        case 'down':
          newOffset.y -= movementAmount;
          break;
        case 'left':
          newOffset.x -= movementAmount;
          break;
        case 'right':
          newOffset.x += movementAmount;
          break;
        case 'closer':
          newOffset.z += movementAmount;
          break;
        case 'farther':
          newOffset.z -= movementAmount;
          break;
      }

      updatePositionOffset(newOffset);
    };

    // Update depth based on slider value
    const updateDepth = (value: number) => {
      // Convert slider value (0-100) to depth range (-2 to 2)
      const depth = (value / 50 - 1) * 2;
      const newOffset = {...positionOffset, z: depth};
      updatePositionOffset(newOffset);
    };

    // Handle VR controller interactions
    const handleSelectStart = (e: any) => {
      if (!inXRSession) return;

      // Toggle the direction menu when target is clicked
      const newMenuState = !showDirectionMenu;
      setShowDirectionMenu(newMenuState);

      // Reset to position mode when opening menu
      if (newMenuState) {
        setMenuMode('position');
      }

      // Notify parent *exercise component* about menu state change immediately
      if (onMenuToggle) {
        onMenuToggle(newMenuState);
      }

      if (newMenuState) {
        // If opening the menu, just return (don't start dragging)
        return;
      }

      // Logic for starting drag (only runs if menu is closing)
      setIsDragging(true);
      const controller = e.controller;
      if (controller?.controller?.grip) {
        const initialControllerPos = controller.controller.grip.position;
        dragStartPosition.current = {
          x: finalPosition.x,
          y: finalPosition.y,
          z: finalPosition.z,
          controllerX: initialControllerPos.x,
          controllerY: initialControllerPos.y,
          controllerZ: initialControllerPos.z,
        };
      }
    };

    const handleSelectEnd = () => {
      if (!inXRSession) return;

      setIsDragging(false);
      dragStartPosition.current = null;
    };

    const handleMove = (e: any) => {
      if (!inXRSession || !isDragging || !dragStartPosition.current) return;

      const controller = e.controller;
      if (!controller?.controller?.grip) return;

      const currentControllerPos = controller.controller.grip.position;

      // Calculate the difference in controller position
      const deltaX = currentControllerPos.x - dragStartPosition.current.controllerX;
      const deltaY = currentControllerPos.y - dragStartPosition.current.controllerY;
      const deltaZ = currentControllerPos.z - dragStartPosition.current.controllerZ;

      // Calculate new offset relative to the original position
      const newOffset = {
        x: deltaX + (dragStartPosition.current.x - position.x),
        y: deltaY + (dragStartPosition.current.y - position.y),
        z: deltaZ + (dragStartPosition.current.z - position.z),
      };

      updatePositionOffset(newOffset);
    };

    // Sync positionOffset with ballPositionStore on mount and when it changes
    useEffect(() => {
      const storeOffset = ballPositionStore.getOffset();
      if (storeOffset && (storeOffset.x !== 0 || storeOffset.y !== 0 || storeOffset.z !== 0)) {
        setPositionOffset(storeOffset);
      }
    }, []); // Run once on mount

    // Continuously sync with ballPositionStore in preview mode (not in VR)
    useFrame(() => {
      if (!inXRSession) {
        const storeOffset = ballPositionStore.getOffset();
        // Only update if values have actually changed to avoid unnecessary re-renders
        if (
          storeOffset &&
          (storeOffset.x !== positionOffset.x ||
            storeOffset.y !== positionOffset.y ||
            storeOffset.z !== positionOffset.z)
        ) {
          setPositionOffset(storeOffset);
        }
      }
    });

    // Only use keyboard controls in dev mode
    useEffect(() => {
      if (!isDevMode) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        const rotationStep = 0.1;
        switch (e.key) {
          case 'ArrowLeft':
            setRotation(prev => ({...prev, y: prev.y - rotationStep}));
            break;
          case 'ArrowRight':
            setRotation(prev => ({...prev, y: prev.y + rotationStep}));
            break;
          case 'ArrowUp':
            setRotation(prev => ({...prev, x: prev.x - rotationStep}));
            break;
          case 'ArrowDown':
            setRotation(prev => ({...prev, x: prev.x + rotationStep}));
            break;
          // Add ball position adjustment via keyboard in dev mode
          case 'w':
            updatePositionOffset({...positionOffset, z: positionOffset.z - 0.1});
            break;
          case 's':
            updatePositionOffset({...positionOffset, z: positionOffset.z + 0.1});
            break;
          case 'a':
            updatePositionOffset({...positionOffset, x: positionOffset.x - 0.1});
            break;
          case 'd':
            updatePositionOffset({...positionOffset, x: positionOffset.x + 0.1});
            break;
          case 'q':
            updatePositionOffset({...positionOffset, y: positionOffset.y + 0.1});
            break;
          case 'e':
            updatePositionOffset({...positionOffset, y: positionOffset.y - 0.1});
            break;
          case 'r':
            // Reset position
            updatePositionOffset({x: 0, y: 0, z: 0});
            break;
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isDevMode, positionOffset, onPositionChange]);

    // Render peripheral markers for MFBF
    const renderPeripheralMarkers = () => {
      if (!showPeripheral) return null;

      const markers = [];
      const radius = 0.5;
      const count = 8;

      for (let i = 0; i < count; i++) {
        const angle = (i / count) * Math.PI * 2;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;

        markers.push(
          <group key={i} position={[x, y, 0]}>
            {/* Left eye markers */}
            {(eyeToTrain === 'left' || eyeToTrain === 'both') && (
              <mesh layers={Layer.LEFT}>
                <boxGeometry args={[0.02, 0.02, 0.02]} />
                <meshStandardMaterial color="white" />
              </mesh>
            )}

            {/* Right eye markers */}
            {(eyeToTrain === 'right' || eyeToTrain === 'both') && (
              <mesh layers={Layer.RIGHT}>
                <boxGeometry args={[0.02, 0.02, 0.02]} />
                <meshStandardMaterial color="white" />
              </mesh>
            )}
          </group>
        );
      }

      return markers;
    };

    // Calculate final position with offset
    const finalPosition = new Vector3(
      position.x + positionOffset.x,
      position.y + positionOffset.y,
      position.z + positionOffset.z
    );

    // Render the training target with appropriate wrappers based on XR context
    const targetMesh = (
      <Interactive onSelectStart={handleSelectStart} onSelectEnd={handleSelectEnd} onMove={handleMove}>
        <mesh ref={targetRef} layers={Layer.LEFT}>
          <sphereGeometry args={[0.1, 32, 32]} />
          <meshStandardMaterial
            color={finalLeftEyeColor}
            metalness={0}
            roughness={1}
            emissive={finalLeftEyeColor}
            emissiveIntensity={0.2}
            flatShading={true}
          />
        </mesh>
      </Interactive>
    );

    // Update close menu function to also notify parent
    const closeMenu = () => {
      setShowDirectionMenu(false);
      // Notify parent *exercise component* directly on close action
      if (onMenuToggle) {
        onMenuToggle(false);
      }
    };

    // Initialize slider value when menu opens
    useEffect(() => {
      if (showDirectionMenu) {
        const value = (positionOffset.z / 2 + 1) * 50;
        setSliderValue(value);
      }
    }, [showDirectionMenu, positionOffset.z]);

    return (
      <group ref={groupRef} position={finalPosition} scale={scale} rotation={[rotation.x, rotation.y, 0]}>
        {inXRSession ? targetMesh : targetMesh}

        {/* Right eye target */}
        <mesh layers={Layer.RIGHT}>
          <sphereGeometry args={[0.1, 32, 32]} />
          <meshStandardMaterial
            color={finalRightEyeColor}
            metalness={0}
            roughness={1}
            emissive={finalRightEyeColor}
            emissiveIntensity={0.2}
            flatShading={true}
          />
        </mesh>

        {/* Target X marker - Per Eye */}
        {/* Left eye overlay */}
        {finalLeftEyeOverlay && (
          <Text
            position={[0, 0, 0.1]}
            scale={0.15}
            layers={Layer.LEFT}
            color="yellow"
            anchorX="center"
            anchorY="middle"
            outlineColor="yellow"
            outlineWidth={0.001}
            renderOrder={1}
            material-depthTest={false}
          >
            x
          </Text>
        )}

        {/* Right eye overlay */}
        {finalRightEyeOverlay && (
          <Text
            position={[0, 0, 0.1]}
            scale={0.15}
            layers={Layer.RIGHT}
            color="yellow"
            anchorX="center"
            anchorY="middle"
            outlineColor="yellow"
            outlineWidth={0.001}
            renderOrder={1}
            material-depthTest={false}
          >
            x
          </Text>
        )}

        {/* Peripheral markers for MFBF */}
        {renderPeripheralMarkers()}

        {/* VR Settings Menu */}
        <VRSettingsMenu
          visible={showDirectionMenu && inXRSession}
          mode={menuMode}
          onModeChange={setMenuMode}
          onClose={() => {
            setShowDirectionMenu(false);
            if (onMenuToggle) onMenuToggle(false);
          }}
          onEndExercise={onEndExercise}
          onPositionMove={moveTarget}
          onLeftEyeColorChange={setVrLeftEyeColor}
          onRightEyeColorChange={setVrRightEyeColor}
          onLeftEyeOverlayToggle={() => setVrLeftEyeOverlay(!vrLeftEyeOverlay)}
          onRightEyeOverlayToggle={() => setVrRightEyeOverlay(!vrRightEyeOverlay)}
          onSpeedChange={onSpeedChange || (() => {})}
          onRangeChange={onRangeChange || (() => {})}
          leftEyeColor={vrLeftEyeColor}
          rightEyeColor={vrRightEyeColor}
          leftEyeOverlay={vrLeftEyeOverlay}
          rightEyeOverlay={vrRightEyeOverlay}
          currentSpeed={currentSpeed}
          currentRange={currentRange}
          timeRemaining={workoutStore.getTimeRemaining()}
        />

        {/* OLD Direction Menu - REMOVED */}
        {false && showDirectionMenu && inXRSession && (
          <group position={[0, 0.3, 0]}>
            <Interactive onSelect={() => moveTarget('up')}>
              <group position={[0, 0.15, 0]}>
                <mesh>
                  <boxGeometry args={[0.15, 0.15, 0.02]} />
                  <meshStandardMaterial color="blue" emissive="blue" emissiveIntensity={0.5} />
                </mesh>
                <Text position={[0, 0, 0.02]} scale={0.08} color="white" anchorX="center" anchorY="middle">
                  ▲
                </Text>
              </group>
            </Interactive>

            <Interactive onSelect={() => moveTarget('down')}>
              <group position={[0, -0.15, 0]}>
                <mesh>
                  <boxGeometry args={[0.15, 0.15, 0.02]} />
                  <meshStandardMaterial color="blue" emissive="blue" emissiveIntensity={0.5} />
                </mesh>
                <Text position={[0, 0, 0.02]} scale={0.08} color="white" anchorX="center" anchorY="middle">
                  ▼
                </Text>
              </group>
            </Interactive>

            <Interactive onSelect={() => moveTarget('left')}>
              <group position={[-0.15, 0, 0]}>
                <mesh>
                  <boxGeometry args={[0.15, 0.15, 0.02]} />
                  <meshStandardMaterial color="blue" emissive="blue" emissiveIntensity={0.5} />
                </mesh>
                <Text position={[0, 0, 0.02]} scale={0.08} color="white" anchorX="center" anchorY="middle">
                  ◄
                </Text>
              </group>
            </Interactive>

            <Interactive onSelect={() => moveTarget('right')}>
              <group position={[0.15, 0, 0]}>
                <mesh>
                  <boxGeometry args={[0.15, 0.15, 0.02]} />
                  <meshStandardMaterial color="blue" emissive="blue" emissiveIntensity={0.5} />
                </mesh>
                <Text position={[0, 0, 0.02]} scale={0.08} color="white" anchorX="center" anchorY="middle">
                  ►
                </Text>
              </group>
            </Interactive>

            <Interactive
              onSelect={() => {
                updatePositionOffset({x: 0, y: 0, z: 0});
                closeMenu();
              }}
            >
              <group position={[0, 0, 0]}>
                <mesh>
                  <boxGeometry args={[0.12, 0.12, 0.02]} />
                  <meshStandardMaterial color="red" emissive="red" emissiveIntensity={0.5} />
                </mesh>
                <Text position={[0, 0, 0.02]} scale={0.06} color="white" anchorX="center" anchorY="middle">
                  ✖
                </Text>
              </group>
            </Interactive>
          </group>
        )}

        {/* OLD Depth adjustment slider - REMOVED, now in VRSettingsMenu */}
        {false && showDirectionMenu && inXRSession && (
          <group position={[0, -0.25, 0]}>
            <Text position={[0, 0.07, 0]} scale={0.05} color="white" anchorX="center" anchorY="middle">
              Distance
            </Text>

            {/* Slider track */}
            <mesh position={[0, 0, 0]}>
              <boxGeometry args={[0.5, 0.03, 0.01]} />
              <meshStandardMaterial color="#555555" emissive="#555555" emissiveIntensity={0.3} />
            </mesh>

            {/* Slider markers */}
            <Text position={[-0.25, -0.04, 0]} scale={0.03} color="white" anchorX="center" anchorY="middle">
              Far
            </Text>
            <Text position={[0.25, -0.04, 0]} scale={0.03} color="white" anchorX="center" anchorY="middle">
              Near
            </Text>

            {/* Decrement button (Farther) */}
            <Interactive
              onSelect={() => {
                const newValue = Math.max(0, sliderValue - 10);
                setSliderValue(newValue);
                updateDepth(newValue);
              }}
            >
              <group position={[-0.32, 0, 0]}>
                <mesh>
                  <boxGeometry args={[0.08, 0.08, 0.02]} />
                  <meshStandardMaterial color="#3366cc" emissive="#3366cc" emissiveIntensity={0.5} />
                </mesh>
                <Text position={[0, 0, 0.02]} scale={0.05} color="white" anchorX="center" anchorY="middle">
                  -
                </Text>
              </group>
            </Interactive>

            {/* Increment button (Closer) */}
            <Interactive
              onSelect={() => {
                const newValue = Math.min(100, sliderValue + 10);
                setSliderValue(newValue);
                updateDepth(newValue);
              }}
            >
              <group position={[0.32, 0, 0]}>
                <mesh>
                  <boxGeometry args={[0.08, 0.08, 0.02]} />
                  <meshStandardMaterial color="#3366cc" emissive="#3366cc" emissiveIntensity={0.5} />
                </mesh>
                <Text position={[0, 0, 0.02]} scale={0.05} color="white" anchorX="center" anchorY="middle">
                  +
                </Text>
              </group>
            </Interactive>

            {/* Slider knob - interactive */}
            <Interactive
              onSelect={e => {
                // Get pointer position relative to slider
                const pointer = e.intersection?.point;
                if (pointer) {
                  // Calculate local X position (-0.25 to 0.25)
                  const localX = pointer.x - finalPosition.x;
                  // Convert to 0-100 value for slider, clamped
                  const newValue = Math.max(0, Math.min(100, (localX + 0.25) * 200));
                  setSliderValue(newValue);
                  updateDepth(newValue);
                }
              }}
            >
              <group>
                {/* Create a larger invisible hit area for better interaction */}
                <mesh visible={false}>
                  <boxGeometry args={[0.5, 0.1, 0.1]} />
                  <meshStandardMaterial transparent opacity={0} />
                </mesh>

                {/* Visible slider knob positioned based on value */}
                <mesh position={[(sliderValue / 100 - 0.5) * 0.5, 0, 0.01]}>
                  <boxGeometry args={[0.05, 0.07, 0.02]} />
                  <meshStandardMaterial color="#4488ff" emissive="#4488ff" emissiveIntensity={0.6} />
                </mesh>
              </group>
            </Interactive>

            {/* Display current value */}
            <Text position={[0, -0.12, 0]} scale={0.04} color="white" anchorX="center" anchorY="middle">
              {((sliderValue / 50 - 1) * 2).toFixed(1)}
            </Text>

            {/* Time remaining display - Reads GLOBAL pause state */}
            <group position={[0, -0.22, 0]}>
              <mesh position={[0, 0, -0.005]}>
                <boxGeometry args={[0.4, 0.11, 0.01]} />
                <meshStandardMaterial color="#333333" emissive="#333333" emissiveIntensity={0.3} />
              </mesh>
              <Text position={[0, 0.03, 0]} scale={0.035} color="#55aaff" anchorX="center" anchorY="middle">
                Time Remaining
              </Text>
              <Text position={[0, -0.03, 0]} scale={0.05} color="white" anchorX="center" anchorY="middle">
                {`${workoutStore.getTimeRemaining()}s`}
              </Text>
            </group>

            {/* Settings Toolbar */}
            <group position={[0, -0.42, 0]}>
              {/* Speed Control */}
              <group position={[-0.35, 0, 0]}>
                <Text position={[0, 0.08, 0]} scale={0.04} color="white" anchorX="center" anchorY="middle">
                  Speed
                </Text>
                <Interactive
                  onSelect={() => {
                    const newSpeed = Math.max(0, currentSpeed - 1.5);
                    onSpeedChange?.(newSpeed);
                  }}
                >
                  <group position={[-0.08, 0, 0]}>
                    <mesh>
                      <boxGeometry args={[0.06, 0.06, 0.02]} />
                      <meshStandardMaterial color="#3366cc" emissive="#3366cc" emissiveIntensity={0.5} />
                    </mesh>
                    <Text position={[0, 0, 0.02]} scale={0.04} color="white" anchorX="center" anchorY="middle">
                      -
                    </Text>
                  </group>
                </Interactive>
                <Text position={[0, 0, 0]} scale={0.035} color="#ffaa00" anchorX="center" anchorY="middle">
                  {currentSpeed.toFixed(1)}
                </Text>
                <Interactive
                  onSelect={() => {
                    const newSpeed = Math.min(15, currentSpeed + 1.5);
                    onSpeedChange?.(newSpeed);
                  }}
                >
                  <group position={[0.08, 0, 0]}>
                    <mesh>
                      <boxGeometry args={[0.06, 0.06, 0.02]} />
                      <meshStandardMaterial color="#3366cc" emissive="#3366cc" emissiveIntensity={0.5} />
                    </mesh>
                    <Text position={[0, 0, 0.02]} scale={0.04} color="white" anchorX="center" anchorY="middle">
                      +
                    </Text>
                  </group>
                </Interactive>
              </group>

              {/* Range Control */}
              <group position={[0, 0, 0]}>
                <Text position={[0, 0.08, 0]} scale={0.04} color="white" anchorX="center" anchorY="middle">
                  Range
                </Text>
                <Interactive
                  onSelect={() => {
                    const ranges: ('narrow' | 'medium' | 'wide')[] = ['narrow', 'medium', 'wide'];
                    const currentIndex = ranges.indexOf(currentRange);
                    const newIndex = (currentIndex + 1) % 3;
                    onRangeChange?.(ranges[newIndex]);
                  }}
                >
                  <group>
                    <mesh>
                      <boxGeometry args={[0.15, 0.06, 0.02]} />
                      <meshStandardMaterial color="#3366cc" emissive="#3366cc" emissiveIntensity={0.5} />
                    </mesh>
                    <Text position={[0, 0, 0.02]} scale={0.035} color="white" anchorX="center" anchorY="middle">
                      {currentRange.toUpperCase()}
                    </Text>
                  </group>
                </Interactive>
              </group>

              {/* Duration Control */}
              <group position={[0.35, 0, 0]}>
                <Text position={[0, 0.08, 0]} scale={0.04} color="white" anchorX="center" anchorY="middle">
                  Duration
                </Text>
                <Interactive
                  onSelect={() => {
                    const newDuration = Math.max(5, currentDuration - 5);
                    onDurationChange?.(newDuration);
                  }}
                >
                  <group position={[-0.08, 0, 0]}>
                    <mesh>
                      <boxGeometry args={[0.06, 0.06, 0.02]} />
                      <meshStandardMaterial color="#3366cc" emissive="#3366cc" emissiveIntensity={0.5} />
                    </mesh>
                    <Text position={[0, 0, 0.02]} scale={0.04} color="white" anchorX="center" anchorY="middle">
                      -
                    </Text>
                  </group>
                </Interactive>
                <Text position={[0, 0, 0]} scale={0.035} color="#ffaa00" anchorX="center" anchorY="middle">
                  {currentDuration}s
                </Text>
                <Interactive
                  onSelect={() => {
                    const newDuration = Math.min(60, currentDuration + 5);
                    onDurationChange?.(newDuration);
                  }}
                >
                  <group position={[0.08, 0, 0]}>
                    <mesh>
                      <boxGeometry args={[0.06, 0.06, 0.02]} />
                      <meshStandardMaterial color="#3366cc" emissive="#3366cc" emissiveIntensity={0.5} />
                    </mesh>
                    <Text position={[0, 0, 0.02]} scale={0.04} color="white" anchorX="center" anchorY="middle">
                      +
                    </Text>
                  </group>
                </Interactive>
              </group>
            </group>
          </group>
        )}

        {/* Adjustment UI information - show when adjusting position */}
        {showAdjustmentUI && (
          <Html position={[0, 0.25, 0]} center>
            <div
              style={{
                color: 'white',
                fontSize: '14px',
                textAlign: 'center',
                backgroundColor: 'rgba(0,0,0,0.6)',
                padding: '5px',
                borderRadius: '5px',
                width: '180px',
              }}
            >
              <div>Position Adjustment</div>
              <div>X: {positionOffset.x.toFixed(2)}</div>
              <div>Y: {positionOffset.y.toFixed(2)}</div>
              <div>Z: {positionOffset.z.toFixed(2)}</div>
              <div style={{fontSize: '12px', marginTop: '5px'}}>
                {inXRSession ? (
                  <>
                    Use controllers to adjust
                    <br />
                    Click ball to reset
                  </>
                ) : (
                  <>
                    WASD/QE to adjust
                    <br />R to reset
                  </>
                )}
              </div>
            </div>
          </Html>
        )}

        {isDevMode && !inXRSession && (
          <Html position={[0, 0.2, 0]} center>
            <div style={{color: 'white', fontSize: '12px', textAlign: 'center'}}>
              Arrow keys to rotate
              <br />
              WASD/QE to move
            </div>
          </Html>
        )}
      </group>
    );
  }
);
